/* eslint-disable camelcase */
import React, { FC, useEffect } from "react"
import { observer } from "mobx-react-lite"
import {
  Appearance,
  FlatList,
  ImageStyle,
  ListRenderItemInfo,
  StyleProp,
  ViewStyle,
} from "react-native"
import { AutoImage, Screen, Text } from "../../../../components"
import { useStores } from "../../../../models"
import { color } from "../../../../theme"
import { CompositeScreenProps } from "@react-navigation/native"
import { goBack } from "../../../../navigators"
import moment from "moment"
import { View } from "native-base"
import { translate, TxKeyPath } from "../../../../i18n"
import { SafeAreaView } from "react-native-safe-area-context"
import _ from "lodash"
import { getSnapshot } from "mobx-state-tree"

const ROOT: ViewStyle = {
  flex: 1,
}

const FLAT_LIST: ViewStyle = {
  flexGrow: 1
}

const IMG_PROFILE: StyleProp<ImageStyle> = {
  width: 48,
  height: 48,
  borderRadius: 48 / 2,
}

const Item: FC<{ colorScheme?: "light" | "dark", info: ListRenderItemInfo<any> }> = observer(function Item(props) {
  const { colorScheme = "light" || Appearance.getColorScheme(), info } = props;

  const {
    appStore: { farmStore: { users = [] } }
  } = useStores()

  const { item } = info
  const { properties: {
    attributes: { state: attributesState = {}, ...attributesRest } = {},
    old: { state: oldState = {} } = {}
  } } = item;
  const subject = item.causer_type === 'App\\User' && users.find((u) => u.id === item.causer_id) || null
  const stateChanges = {}
  Object.keys(attributesState).forEach((k) => {
    if (attributesState[k] instanceof Object) {
      if (JSON.stringify(attributesState[k]) !== JSON.stringify(oldState[k]))
        stateChanges[k] = attributesState[k];
    } else if (attributesState[k] !== oldState[k])
      stateChanges[k] = attributesState[k];
  })
  const allChanges = { ...attributesRest, ...stateChanges }
  const { updated_at, created_at, updatedAt, uploadedAt, ...changes } = allChanges
  const url = 'https://app.jala.tech/img/cache/medium/';
  const img = !subject || !subject.avatar ? {uri: `${url}/users/default.png`} : { uri: `${url}${subject.avatar}` };
  const createdAtFormatted = moment.utc(item.created_at).local().add(1, 'day').isAfter() ?
    `${moment.utc(item.created_at).local().format('HH:mm:ss')} (${moment.utc(item.created_at).local().fromNow()})` :
    moment.utc(item.created_at).local().format('DD MMM YYYY HH:mm:ss')
  const keys = Object.keys(changes)

  return <View flex={1} flexDirection="row" alignItems="center">
    <View padding="8px" paddingLeft="16px">
      <AutoImage source={img} style={IMG_PROFILE} />
    </View>
    <View flex={1} padding="8px" paddingRight="16px">
      <Text text={createdAtFormatted} preset="xs" colorScheme={colorScheme} />
      <Text height="42px">
        <Text text={`${subject?.name || '-'} `} preset="body2" colorScheme={colorScheme} fontWeight="bold" />
        {!!uploadedAt && [
          <Text key="uploaded-0" text={`${translate('autoFeederPond.uploaded')} `} preset="body2" colorScheme={colorScheme} />,
          <Text key="uploaded-1" text={`data `} preset="body2" fontWeight="bold" colorScheme={colorScheme} />,
        ]}
        {!!uploadedAt && !!keys.length && <Text text={` ${translate('and')} `} preset="body2" colorScheme={colorScheme} />}
        {!!keys.length && [
          <Text key="changes-0" text={`${translate(created_at && 'autoFeederPond.created' || 'autoFeederPond.updated')} `} preset="body2" colorScheme={colorScheme} />,
          keys.map((key, index) => [
            <Text
              key={`changes-key-${index}`}
              text={translate(`autoFeederPond.${key}` as TxKeyPath)}
              preset="body2"
              fontWeight="bold"
              colorScheme={colorScheme}
            />,
            <Text key={`junction-${index}`} text={(index === (keys.length - 1)) ? '' : ', '} preset="body2" colorScheme={colorScheme} />
          ])
        ]}
      </Text>
    </View>
  </View>
})

const ListEmptyComponent: FC<{ colorScheme?: "light" | "dark" }> = observer(function ListEmptyComponent(props) {
  const { colorScheme = "light" || Appearance.getColorScheme() } = props;

  return <View flex={1} alignItems="center" justifyContent="center">
    <Text tx="noData" transformText={_.upperFirst} colorScheme={colorScheme} />
  </View>
})

export const AutoFeederPondActivityListTitle: FC<{ colorScheme?: "light" | "dark", tintColor?: string }> = observer(function AutoFeederPondActivityListTitle(props) {
  const { colorScheme = "light" || Appearance.getColorScheme(), tintColor } = props
  return <View>
    <Text tx="autoFeederPondActivityListScreen.title" preset="title2" colorScheme={colorScheme} color={tintColor} />
    <Text tx="autoFeederPondActivityListScreen.subtitle" preset="body3" colorScheme={colorScheme} color={tintColor} />
  </View>
})

export const AutoFeederPondActivityListScreen: FC<{ colorScheme?: "light" | "dark" } & CompositeScreenProps<any, any>> = observer(function AutoFeederPondActivityListScreen(props) {
  const { colorScheme = "light" || Appearance.getColorScheme() } = props

  // Pull in one of our MST stores
  const {
    appStore: {
      farmStore: {
        pondStore: {
          autoFeederPondStore,
          autoFeederPondStore: { activities }
        }
      }
    }
  } = useStores()

  useEffect(() => {
    if (!autoFeederPondStore) return goBack('Home.Stack');
    autoFeederPondStore.getActivities()
  }, [autoFeederPondStore])

  return (
    <Screen
      style={ROOT}
      preset="fixed"
      unsafe
      colorScheme={colorScheme}
      backgroundBar={colorScheme === 'light' ? color.primary : undefined}
      statusBar={colorScheme === 'light' ? 'light-content' : 'dark-content'}
    >
      <FlatList
        data={getSnapshot(activities)}
        renderItem={(info) => (
          <Item colorScheme={colorScheme} info={info} />
        )}
        contentContainerStyle={FLAT_LIST}
        ListEmptyComponent={<ListEmptyComponent colorScheme={colorScheme} />}
        ListFooterComponent={<SafeAreaView edges={['bottom']} />}
      />
    </Screen>
  )
})
